import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Status() {
  const [state, setState] = useState({ phase: "checking", data: null, error: null, checkedAt: null });

  const check = async () => {
    setState((s) => ({ ...s, phase: "checking" }));
    try {
      const t0 = performance.now();
      const { data } = await axios.get(`${API}/health`, { timeout: 8000 });
      const latency = Math.round(performance.now() - t0);
      setState({ phase: "ok", data: { ...data, latency_ms: latency }, error: null, checkedAt: new Date() });
    } catch (e) {
      setState({ phase: "down", data: null, error: e?.message || "Unreachable", checkedAt: new Date() });
    }
  };

  useEffect(() => { check(); const t = setInterval(check, 15000); return () => clearInterval(t); }, []);

  const isUp = state.phase === "ok";
  const isChecking = state.phase === "checking";

  return (
    <div className="min-h-screen bg-[#0c0a09] flex items-center justify-center px-4" data-testid="status-page">
      <div className="w-full max-w-lg">
        <div className="mb-10 text-center">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-500/80">System Status</div>
          <h1 className="font-serif text-5xl mt-2 tracking-tight text-stone-50">Copilot</h1>
        </div>

        <div className="border border-stone-800 bg-[#1c1917] p-8" data-testid="status-card">
          <div className="flex items-center gap-3">
            <span
              className={`w-2.5 h-2.5 ${isUp ? "bg-green-500" : isChecking ? "bg-amber-400 animate-pulse" : "bg-red-500"}`}
              data-testid="status-dot"
            />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-stone-500">
              {isChecking ? "checking" : isUp ? "operational" : "unreachable"}
            </span>
          </div>

          <h2 className="mt-4 font-serif text-3xl text-stone-100" data-testid="status-headline">
            {isChecking ? "Checking systems…" : isUp ? "All systems operational." : "We're back in a moment."}
          </h2>
          <p className="mt-2 text-sm text-stone-400 leading-relaxed" data-testid="status-detail">
            {isUp
              ? "The Copilot API is responding normally. Your assistant is on."
              : isChecking
              ? "Reaching out to the Copilot API…"
              : "Copilot can't be reached from this browser right now. This page will keep retrying every 15 seconds. If it persists, your admin has already been paged."}
          </p>

          <dl className="mt-8 grid grid-cols-2 gap-y-3 gap-x-6 font-mono text-xs">
            <dt className="text-stone-500 uppercase tracking-widest text-[10px]">Endpoint</dt>
            <dd className="text-stone-200 truncate" data-testid="status-endpoint">{API}/health</dd>

            <dt className="text-stone-500 uppercase tracking-widest text-[10px]">Latency</dt>
            <dd className="text-stone-200" data-testid="status-latency">
              {isUp ? `${state.data.latency_ms} ms` : "—"}
            </dd>

            <dt className="text-stone-500 uppercase tracking-widest text-[10px]">Server time</dt>
            <dd className="text-stone-200" data-testid="status-server-time">
              {isUp ? new Date(state.data.time).toLocaleString() : "—"}
            </dd>

            <dt className="text-stone-500 uppercase tracking-widest text-[10px]">Last checked</dt>
            <dd className="text-stone-200" data-testid="status-last-check">
              {state.checkedAt ? state.checkedAt.toLocaleTimeString() : "—"}
            </dd>
          </dl>

          <div className="mt-8 flex items-center gap-3">
            <button
              data-testid="status-recheck"
              onClick={check}
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white transition-colors"
            >
              Check again
            </button>
            <Link
              data-testid="status-home"
              to="/"
              className="text-[10px] uppercase tracking-widest font-mono px-4 py-2 border border-stone-700 hover:border-stone-500 text-stone-300 transition-colors"
            >
              Back to dashboard
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-[10px] font-mono uppercase tracking-widest text-stone-600">
          this page auto-refreshes every 15s
        </p>
      </div>
    </div>
  );
}
